
import React, { useState, useEffect, useRef } from "react";
import PromptGrid from "@/components/prompts/PromptGrid";
import LoadingState from "./LoadingState";
import EmptyState from "./EmptyState";
import ResultsSummary from "./ResultsSummary";
import ExplorePagination from "./ExplorePagination";
import { Button } from "@/components/ui/button";
import { RefreshCcw } from "lucide-react";
import type { Prompt } from "@/services/promptService";

interface ExploreContentProps {
  isLoading: boolean;
  prompts: Prompt[];
  searchQuery: string;
  currentPage: number;
  totalPrompts: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  itemsPerPage: number;
  hasError?: boolean;
  isCategoryFiltering?: boolean;
}

const ExploreContent: React.FC<ExploreContentProps> = ({
  isLoading,
  prompts,
  searchQuery,
  currentPage,
  totalPrompts,
  totalPages,
  setCurrentPage,
  itemsPerPage,
  hasError = false,
  isCategoryFiltering = false
}) => {
  const prevLoadingRef = useRef(isLoading);
  
  const [showUpdating, setShowUpdating] = useState(false);
  const updatingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const loadingStartTimeRef = useRef<number | null>(null);
  
  useEffect(() => {
    console.log(`ExploreContent: isLoading=${isLoading}, prompts=${prompts.length}, totalPrompts=${totalPrompts}`);
  }, [isLoading, prompts.length, totalPrompts]);
  
  useEffect(() => {
    // Only consider showing updating message if we have initial content
    const shouldConsiderShowingUpdating = isLoading && prompts.length > 0;
    
    if (updatingTimerRef.current) {
      clearTimeout(updatingTimerRef.current);
      updatingTimerRef.current = null;
    }
    
    if (shouldConsiderShowingUpdating) {
      if (!prevLoadingRef.current) {
        loadingStartTimeRef.current = Date.now();
      }
      
      // Add a significant delay before showing "Updating results..." message
      updatingTimerRef.current = setTimeout(() => {
        if (isLoading) {
          setShowUpdating(true);
        }
      }, 1200); // Adjusted for better UX
    } else if (prevLoadingRef.current && !isLoading) {
      const loadingTime = loadingStartTimeRef.current 
        ? Date.now() - loadingStartTimeRef.current 
        : 0;
      
      if (loadingTime < 500) {
        setShowUpdating(false);
      } else {
        // Add a small delay before hiding the message to prevent rapid toggling
        const hideDelay = Math.min(loadingTime / 3, 800);
        updatingTimerRef.current = setTimeout(() => {
          setShowUpdating(false);
        }, hideDelay);
      }
      
      loadingStartTimeRef.current = null;
    } else {
      setShowUpdating(false);
    }
    
    prevLoadingRef.current = isLoading;
    
    return () => {
      if (updatingTimerRef.current) {
        clearTimeout(updatingTimerRef.current);
      }
    };
  }, [isLoading, prompts.length]);
  
  if (isLoading && !isCategoryFiltering && prompts.length === 0) {
    return <LoadingState />;
  }
  
  if (hasError) {
    return (
      <div className="text-center py-16">
        <h3 className="text-xl font-medium mb-4">Unable to load prompts</h3>
        <p className="text-muted-foreground mb-6">
          There was an issue loading the prompts. Please try again.
        </p>
        <Button 
          onClick={() => window.location.reload()}
          className="flex items-center gap-2"
        >
          <RefreshCcw className="h-4 w-4" />
          Refresh Page
        </Button>
      </div>
    );
  }
  
  if (!prompts || prompts.length === 0) {
    return <EmptyState searchQuery={searchQuery} />;
  }
  
  return (
    <>
      {showUpdating && (
        <div className="mb-4 text-sm text-primary flex items-center justify-start animate-fade-in">
          <RefreshCcw className="h-4 w-4 mr-2 animate-spin" />
          Updating results...
        </div>
      )}
      
      <ResultsSummary
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalItems={totalPrompts}
        itemCount={prompts.length}
        searchQuery={searchQuery}
      />
      
      <PromptGrid prompts={prompts} columns={3} />
      
      {totalPages > 1 && (
        <ExplorePagination 
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
        />
      )}
    </>
  );
};

export default React.memo(ExploreContent);
